import { deserializeData } from '../store';
import type { SerializeData } from '../store';
import type { StorageProvider } from './provider';

const DB_NAME = 'issue-form-storage';
const STORE_NAME = 'file-handles';
const HANDLE_KEY = 'last-file-handle';

async function getPersistedHandle(): Promise<FileSystemFileHandle | null> {
  if (!('indexedDB' in window)) {
    return null;
  }

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);

    request.onupgradeneeded = () => {
      request.result.createObjectStore(STORE_NAME);
    };

    request.onsuccess = () => {
      const db = request.result;
      const transaction = db.transaction(STORE_NAME, 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const getRequest = store.get(HANDLE_KEY);

      getRequest.onsuccess = () =>
        resolve(getRequest.result as FileSystemFileHandle | null);
      getRequest.onerror = () => reject(getRequest.error);
      transaction.onabort = () =>
        reject(transaction.error ?? new Error('IndexedDB transaction aborted'));
    };

    request.onerror = () => reject(request.error);
  });
}

async function persistHandle(handle: FileSystemFileHandle) {
  if (!('indexedDB' in window)) {
    return;
  }

  return new Promise<void>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);

    request.onupgradeneeded = () => {
      request.result.createObjectStore(STORE_NAME);
    };

    request.onsuccess = () => {
      const db = request.result;
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const putRequest = store.put(handle, HANDLE_KEY);

      putRequest.onsuccess = () => resolve();
      putRequest.onerror = () => reject(putRequest.error);
      transaction.onabort = () =>
        reject(transaction.error ?? new Error('IndexedDB transaction aborted'));
    };

    request.onerror = () => reject(request.error);
  });
}

/**
 * Uses the `window.showOpenFilePicker` and `window.showSaveFilePicker` APIs to save and load data.
 *
 * Note: Currently only works in Chromium based browsers (Chrome, Edge, etc.).
 */
export const filePickerProvider: StorageProvider = {
  async loadData() {
    const persistedHandle = await getPersistedHandle();

    if (persistedHandle) {
      try {
        const file = await persistedHandle.getFile();
        const data = await file.text();
        return deserializeData(JSON.parse(data));
      } catch (error) {
        console.warn(
          'Could not reuse the persisted file handle, prompting for a new file instead.',
          error,
        );
      }
    }

    const [fileHandle] = await window.showOpenFilePicker();
    await persistHandle(fileHandle);
    const file = await fileHandle.getFile();
    const data = await file.text();
    return deserializeData(JSON.parse(data));
  },

  async saveData(data: SerializeData) {
    let fileHandle = await getPersistedHandle();

    if (!fileHandle) {
      fileHandle = await window.showSaveFilePicker().catch(() => null);
      if (!fileHandle) return;
      await persistHandle(fileHandle);
    }

    try {
      const writable = await fileHandle.createWritable();
      await writable.write(JSON.stringify(data));
      await writable.close();
    } catch (error) {
      console.warn(
        'Could not write to the persisted handle, prompting for a new file instead.',
        error,
      );
      fileHandle = await window.showSaveFilePicker().catch(() => null);
      if (!fileHandle) return;
      await persistHandle(fileHandle);
      const writable = await fileHandle.createWritable();
      await writable.write(JSON.stringify(data));
      await writable.close();
    }
  },

  async saveAs(data: SerializeData) {
    const fileHandle = await window.showSaveFilePicker();
    const writable = await fileHandle.createWritable();
    await persistHandle(fileHandle);
    await writable.write(JSON.stringify(data));
    await writable.close();
  },
};
